import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import {
  formatCurrency,
  formatDateTime,
  formatPercentage,
  isArray,
} from 'utils/helper';
import { calculateOvertimeUnitRate, getColumnConf } from 'utils/common';
import { summarizeBudgetActuals } from 'utils/budgetActuals';
import SwimlanePage from '../SwimlanePage';
import CornerSpinner from 'components/CornerSpinner';
import { useBudgetQueries } from 'utils/api/queries';
import ProductionAssumptionsPage from '../ProductionAssumptionsPage';
import UsedGlobalsPage from '../UsedGlobalsPage';
import { buildTopLevelSectionSummaryRows } from '../sectionSummary';
import { formatPrintTags } from '../detailValues';
import {
  filterPrintDetailColumns,
  getPrintDetailColumnWidths,
} from '../printFields';
import ProductionInfoBlocks from '../ProductionInfoBlocks';
import {
  getPrintPaperClassName,
  getPrintPaperCssVariables,
} from 'utils/printPaperSizes';

import './styles.scss';

const MotionPictureBudget = React.forwardRef(
  (
    {
      paperSize,
      paperOptions,
      includeComments,
      includeCineBudgetBranding,
      showProductionInfoHeaders,
      showJobInformationSection,
      showProductionInformationSection,
      jobInformationSection,
      productionInformationSection,
      onJobInformationChange,
      onProductionInformationChange,
      canEditProductionInfoSections,
      includeProductionAssumptions,
      productionAssumptionsTitle,
      productionAssumptions,
      onProductionAssumptionsTitleChange,
      onProductionAssumptionsChange,
      canEditProductionAssumptions,
      includeUsedGlobals,
      usedGlobals,
      includeAccountNumbers,
      includeActuals,
      includeFringeDetails,
      includeOvertimeDetails,
      visibleOvertimeSlots,
      visibleDetailFields,
      budgetData,
      isLoading,
      getLabel,
    },
    ref
  ) => {
    const { selectedBudget } = useHomeScreenContext();
    const printPaperClassName = getPrintPaperClassName(paperSize, paperOptions);
    const printPaperStyle = getPrintPaperCssVariables(paperSize, paperOptions);
    const columns = filterPrintDetailColumns(
      getColumnConf(
        selectedBudget,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        includeAccountNumbers,
        {},
        undefined,
        includeOvertimeDetails
      ),
      visibleDetailFields,
      visibleOvertimeSlots
    );
    const detailColumns = columns
      .filter(col => col.key !== 'comment' || includeComments)
      .filter(col => col.key !== 'actual' || includeActuals)
      .filter(col => col.key !== 'fringes');
    const printableDetailColumns = detailColumns.flatMap(column =>
      column.key === 'comment' && includeFringeDetails
        ? [{ key: 'fringes' }, column]
        : [column]
    );
    const detailColumnWidths = getPrintDetailColumnWidths(
      printableDetailColumns
    );
    const [budgetTotal, setBudgetTotal] = useState(0);
    const [, setContingency] = useState(0);
    const actualSummary = React.useMemo(
      () => summarizeBudgetActuals(budgetData),
      [budgetData]
    );
    const showActualTotal = includeActuals && actualSummary.hasActuals;

    // Fetch contractual charges
    const { data: contractualChargesData } =
      useBudgetQueries.useContractualChargesQuery({
        budgetId: selectedBudget?.budget,
        enabled: Boolean(selectedBudget?.budget),
      });
    const contractualCharges = Array.isArray(contractualChargesData)
      ? contractualChargesData
      : contractualChargesData?.data || [];
    // Sort by position
    const sortedContractualCharges = [...contractualCharges].sort(
      (a, b) => (a.position || 0) - (b.position || 0)
    );
    const contractualChargesTotal = sortedContractualCharges.reduce(
      (sum, charge) => sum + parseFloat(charge?.total_cost || 0),
      0
    );
    const { data: filmIncentivesData } =
      useBudgetQueries.useFilmIncentivesQuery({
        budgetId: selectedBudget?.budget,
        enabled: Boolean(selectedBudget?.budget),
      });
    const filmIncentives = Array.isArray(filmIncentivesData)
      ? filmIncentivesData
      : filmIncentivesData?.data || [];
    const sortedFilmIncentives = [...filmIncentives].sort(
      (a, b) => (a.position || 0) - (b.position || 0)
    );

    useEffect(() => {
      const fringeTotal = budgetData?.budget_swimlane?.reduce(
        (total, swimlane) => {
          return (
            total +
            (swimlane?.swimlane_detail_sheets?.reduce(
              (sum, sheet) => sum + parseFloat(sheet?.fringe_total || 0),
              0
            ) || 0)
          );
        },
        0
      );
      const totalCost =
        budgetData?.budget_swimlane?.reduce((total, swimlane) => {
          total = total + parseFloat(swimlane?.cost || 0);

          return total;
        }, 0) || 0;

      setBudgetTotal(totalCost - fringeTotal);
      calculateContingency(totalCost);
    }, [budgetData]);
    const getAllFringes = () => {
      const allFringes = [];
      budgetData?.budget_swimlane?.forEach(swimlane => {
        swimlane?.swimlane_detail_sheets?.forEach(sheet => {
          if (sheet?.fringe && Array.isArray(sheet.fringe)) {
            allFringes.push(...sheet.fringe);
          }
        });
      });

      const byId = new Map();
      for (const fringe of allFringes) {
        if (fringe?.id == null) continue;
        if (!byId.has(fringe.id)) byId.set(fringe.id, fringe);
      }

      return Array.from(byId.values()); // unique list
    };

    const renderProductionDetails = () => (
      <ProductionInfoBlocks
        selectedBudget={selectedBudget}
        showProductionInfoHeaders={showProductionInfoHeaders}
        showJobInformationSection={showJobInformationSection}
        showProductionInformationSection={showProductionInformationSection}
        jobInformationSection={jobInformationSection}
        productionInformationSection={productionInformationSection}
        onJobInformationChange={onJobInformationChange}
        onProductionInformationChange={onProductionInformationChange}
        canEditProductionInfoSections={canEditProductionInfoSections}
        includeFringeDetails={includeFringeDetails}
        fringes={includeFringeDetails ? getAllFringes() : []}
      />
    );

    const calculateContingency = total => {
      setContingency(total * 0.1);
    };

    const printLayoutHeader = () => {
      return (
        <div className="print-layout__header">
          <div className="budget-name">
            {budgetData?.name || selectedBudget?.full_name}
          </div>
          <div className="page-counter"></div>
        </div>
      );
    };

    const printLayoutFooter = () => {
      return (
        <div className="print-layout__footer">
          <div className="left-section">
            {selectedBudget?.company_name} CONFIDENTIAL
          </div>
          <div className="middle-section">
            {includeCineBudgetBranding && 'Created with CineBudget Pro'}
          </div>
          <div className="right-section">
            Prepared by{' '}
            {selectedBudget?.last_edited_by || selectedBudget?.created_by_name},{' '}
            {formatDateTime(
              selectedBudget?.updated_on || selectedBudget?.created_on,
              'MMMM Do, YYYY'
            )}
          </div>
        </div>
      );
    };

    const renderSwimlaneSummaryFooter = footerRow => {
      const isGrandTotal = ['Grand Total', 'Net Production Cost'].includes(
        footerRow?.label
      );

      return (
        <tr className={isGrandTotal ? 'grand-total-row' : ''}>
          <td colSpan={includeAccountNumbers ? 2 : 1}>{footerRow?.label}</td>
          <td>
            {footerRow?.displayValue ||
              formatCurrency(
                footerRow?.value || 0,
                selectedBudget?.primary_currency
              )}
          </td>
        </tr>
      );
    };

    const swimlaneSummaryHeader = () => {
      return (
        <thead>
          <tr>
            {includeAccountNumbers && (
              <th style={{ background: '#4285F4', color: 'white' }}>
                Account #
              </th>
            )}
            <th style={{ background: '#4285F4', color: 'white' }}>Category</th>
            <th style={{ background: '#4285F4', color: 'white' }}>Total</th>
          </tr>
        </thead>
      );
    };

    const renderSwimlanes = (swimlane, tabIndex) => {
      return (
        <tr key={`swimlane-${swimlane?.id}-${tabIndex}`}>
          {includeAccountNumbers && <td>{swimlane?.account_number || ''}</td>}
          <td>{swimlane?.name}</td>
          <td>
            {formatCurrency(
              swimlane?.cost || 0,
              selectedBudget?.primary_currency
            )}
          </td>
        </tr>
      );
    };

    const renderTopLevelSection = (section, tabIndex) => {
      return (
        <tr
          className="top-level-section-summary-row"
          key={`top-level-section-${section?.id}-${tabIndex}`}
        >
          {includeAccountNumbers && <td />}
          <td>{section?.name || 'Section'}</td>
          <td>
            {formatCurrency(
              section?.cost || 0,
              selectedBudget?.primary_currency
            )}
          </td>
        </tr>
      );
    };

    const buildSwimlaneSummaryRows = () =>
      buildTopLevelSectionSummaryRows({
        budgetData,
        renderSwimlane: renderSwimlanes,
        renderSection: renderTopLevelSection,
      });

    const appendActualTotalRow = rows =>
      showActualTotal
        ? [
            ...rows,
            {
              rowData: {
                label: 'Actual Total',
                value: actualSummary.actualTotal,
              },
              type: 'swimlaneFooter',
              renderMethod: renderSwimlaneSummaryFooter,
            },
          ]
        : rows;

    const appendFilmIncentiveRows = (rows, grandTotal) => {
      if (!sortedFilmIncentives.length) return rows;
      let totalCredits = 0;
      let incomplete = false;
      const incentiveRows = sortedFilmIncentives.map(incentive => {
        if (!incentive.estimate_complete) incomplete = true;
        const credit = incentive.estimate_complete
          ? parseFloat(incentive.net_credit || 0)
          : 0;
        const rateLabel =
          incentive.rate_percent === null ||
          incentive.rate_percent === undefined ||
          incentive.rate_percent === ''
            ? ''
            : ` (${formatPercentage(incentive.rate_percent)}%)`;
        totalCredits += credit;
        return {
          rowData: {
            label: `${incentive.name}${rateLabel}`,
            value: -credit,
            displayValue: incentive.estimate_complete ? null : 'Incomplete',
          },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        };
      });
      return [
        ...rows,
        ...incentiveRows,
        {
          rowData: {
            label: 'Net Production Cost',
            value: incomplete ? 0 : grandTotal - totalCredits,
            displayValue: incomplete ? 'Incomplete' : null,
          },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        },
      ];
    };

    const getSwimlaneSummaryWithFringes = () => {
      let swimlaneSummary = buildSwimlaneSummaryRows();

      const fringeTotal = budgetData?.budget_swimlane?.reduce(
        (total, swimlane) => {
          return (
            total +
            (swimlane?.swimlane_detail_sheets?.reduce(
              (sum, sheet) => sum + parseFloat(sheet?.fringe_total || 0),
              0
            ) || 0)
          );
        },
        0
      );

      swimlaneSummary = [
        ...swimlaneSummary,
        {
          rowData: { label: 'Sub Total', value: budgetTotal },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        },
        {
          rowData: { label: 'Fringe Total', value: fringeTotal },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        },
        // Add contractual charges rows
        ...sortedContractualCharges.map((charge, index) => ({
          rowData: {
            label: `${charge.name}${
              charge.charge_type === 'percentage'
                ? ` (${formatPercentage(charge.percentage)}%)`
                : ' (Flat Fee)'
            }`,
            value: charge?.total_cost || 0,
          },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        })),
        {
          rowData: {
            label: 'Grand Total',
            value: budgetTotal + fringeTotal + contractualChargesTotal,
          },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        },
      ];

      return appendActualTotalRow(
        appendFilmIncentiveRows(
          swimlaneSummary,
          budgetTotal + fringeTotal + contractualChargesTotal
        )
      );
    };

    const getSwimlaneSummaryWithoutFringes = () => {
      let swimlaneSummary = buildSwimlaneSummaryRows();

      const fringeTotal = budgetData?.budget_swimlane?.reduce(
        (total, swimlane) => {
          return (
            total +
            (swimlane?.swimlane_detail_sheets?.reduce(
              (sum, sheet) => sum + parseFloat(sheet?.fringe_total || 0),
              0
            ) || 0)
          );
        },
        0
      );

      swimlaneSummary = [
        ...swimlaneSummary,
        {
          rowData: { label: 'Sub Total', value: budgetTotal },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        },
        // Add contractual charges rows
        ...sortedContractualCharges.map((charge, index) => ({
          rowData: {
            label: `${charge.name}${
              charge.charge_type === 'percentage'
                ? ` (${formatPercentage(charge.percentage)}%)`
                : ' (Flat Fee)'
            }`,
            value: charge?.total_cost || 0,
          },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        })),
        {
          rowData: {
            label: 'Grand Total',
            value: budgetTotal + fringeTotal + contractualChargesTotal,
          },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        },
      ];

      return appendActualTotalRow(
        appendFilmIncentiveRows(
          swimlaneSummary,
          budgetTotal + fringeTotal + contractualChargesTotal
        )
      );
    };

    const detailsTableHeader = () => {
      return (
        <thead>
          <tr>
            {detailColumns.map(col => {
              // If this is the Notes column and we want to show fringes
              if (col.key === 'comment' && includeFringeDetails) {
                return (
                  <>
                    <th key="fringe-total-header">Fringe Total</th>
                    <th key={`header-${col.key}`}>{getLabel(col.label)}</th>
                  </>
                );
              }
              if (col.key === 'fringes') {
                return <th key={`header-${col.key}`}>Fringes</th>;
              }
              return <th key={`header-${col.key}`}>{getLabel(col.label)}</th>;
            })}
          </tr>
        </thead>
      );
    };

    const getVisibleDetailColumnCount = () => {
      return printableDetailColumns.length || 1;
    };

    const getCategoryLabel = category => {
      const accountNumber = includeAccountNumbers
        ? category?.account_number?.trim?.()
        : '';

      return [accountNumber, category?.name || ''].filter(Boolean).join(' ');
    };

    const getCategoryLabelWithTotal = category =>
      `${getCategoryLabel(category)} (${formatCurrency(
        category?.cost || 0,
        selectedBudget?.primary_currency
      )})`;

    const getDetailColumnValue = (rowData, col) => {
      if (col.isOvertimeRate) {
        return calculateOvertimeUnitRate(
          rowData,
          selectedBudget,
          col.overtimeSlot
        );
      }

      if (col.key === 'tags') {
        return formatPrintTags(rowData?.tags);
      }

      if (col.key === 'column_total') {
        return (
          rowData?.column_total ??
          parseFloat(rowData?.item_total || 0) +
            parseFloat(rowData?.fringe_total || 0)
        );
      }

      if (col.key === 'comment') {
        if (!rowData?.[col.key]) return '';
        return rowData[col.key].length > 22
          ? rowData[col.key].slice(0, 22).concat('...')
          : rowData[col.key];
      }

      return rowData?.[col.key] ?? '';
    };

    const renderDetailSheet = (key, rowData, className) => {
      return (
        <tr className={className} key={key}>
          {detailColumns.map((col, index) => {
            const value = getDetailColumnValue(rowData, col);
            const formattedValue = col.formatValue
              ? col.formatValue(value)
              : value;
            // If this is the Notes column and we want to show fringes
            if (col.key === 'comment' && includeFringeDetails) {
              return (
                <>
                  <td key={`fringe-total-${rowData?.id}-${key}`}>
                    {rowData?.fringe_total
                      ? formatCurrency(rowData?.fringe_total || 0)
                      : ''}
                  </td>
                  <td
                    key={`${col.key}-row-${rowData?.id}-${key}-${index}`}
                    className={col.key === 'item' ? 'item-name-column' : ''}
                  >
                    {col.key !== 'actual' || formattedValue ? col.prefix : ''}
                    {formattedValue}
                  </td>
                </>
              );
            }
            return (
              <td
                key={`${col.key}-row-${rowData?.id}-${key}-${index}`}
                className={col.key === 'item' ? 'item-name-column' : ''}
              >
                {col.key !== 'actual' && !col.isOvertimeRate
                  ? col.prefix
                  : formattedValue
                  ? col.prefix
                  : ''}
                {formattedValue}
              </td>
            );
          })}
        </tr>
      );
    };

    const renderLineItemDetails = (key, rowData, className, colSpan) => {
      return (
        <tr className={className} key={key}>
          <td colSpan={colSpan}>
            {rowData[0]} &gt;&nbsp;
            <strong>{rowData?.slice(1)?.join(' > ')}</strong>
          </td>
        </tr>
      );
    };

    const renderSwimlaneDetails = (key, rowData, className, colSpan) => {
      return (
        <tr className={className} key={key}>
          <td colSpan={colSpan}>{rowData?.join(' ')}</td>
        </tr>
      );
    };

    const getSwimlaneItemDetails = (
      lineItems,
      swimlaneItems,
      swimlaneKey = '',
      detailsCollection = {}
    ) => {
      swimlaneItems = isArray(swimlaneItems) ? swimlaneItems : [];
      let details;

      // sortArray(lineItems, 'id')
      lineItems?.forEach(item => {
        const collectionKey = `${swimlaneKey}->${item.id}`;
        const collectionItems = [...swimlaneItems, item];

        if (item?.swimlane_item_detail_sheets?.length) {
          details = item?.swimlane_item_detail_sheets;
          detailsCollection[collectionKey] = {
            details,
            collectionItems: collectionItems,
          };
        } else {
          detailsCollection = getSwimlaneItemDetails(
            item?.children,
            collectionItems,
            collectionKey,
            detailsCollection
          );
        }
      });

      return detailsCollection;
    };

    const detailSheetRows = detailSheet => {
      return (
        // sortArray(detailSheet, 'id')
        detailSheet?.map((item, index) => {
          return {
            rowData: item,
            key: `detail-sheet-${item?.id}-${index}`,
            type: 'detailed-sheet-row',
          };
        }) || []
      );
    };

    const lineItemDetailsRows = (swimlane, lineItems) => {
      const swimlaneName = getCategoryLabel(swimlane);
      const detailsCollection =
        getSwimlaneItemDetails(lineItems, [], swimlane?.id) || {};

      return Object.keys(detailsCollection)?.reduce(
        (acc, collectionKey) => [
          ...acc,
          {
            rowData: [
              swimlaneName,
              ...(detailsCollection[collectionKey]?.collectionItems?.map(item =>
                getCategoryLabelWithTotal(item)
              ) || []),
            ],
            colSpan: getVisibleDetailColumnCount(),
            type: 'line-item-row',
            key: collectionKey,
          },
          ...detailSheetRows(detailsCollection[collectionKey]?.details),
        ],
        []
      );
    };

    const tableRows = swimlaneData => {
      return swimlaneData?.reduce(
        (acc, swimlane) => [
          ...acc,
          {
            rowData: [getCategoryLabelWithTotal(swimlane)],
            colSpan: getVisibleDetailColumnCount(),
            type: 'swimlane-row',
            key: `swimlane-details-${swimlane?.id}`,
          },
          ...(swimlane?.swimlane_detail_sheets?.length > 0
            ? detailSheetRows(swimlane?.swimlane_detail_sheets)
            : lineItemDetailsRows(
                swimlane,
                swimlane?.budget_swimlane_lineitem
              )),
        ],
        []
      );
    };

    const getBudgetFooterRows = () => {
      // Calculate fringe total
      const fringeTotal = budgetData?.budget_swimlane?.reduce(
        (total, swimlane) => {
          return (
            total +
            (swimlane?.swimlane_detail_sheets?.reduce(
              (sum, sheet) => sum + parseFloat(sheet?.fringe_total || 0),
              0
            ) || 0)
          );
        },
        0
      );

      const rows = [
        {
          rowData: { label: 'Sub Total', value: budgetTotal },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        },
      ];

      // Conditionally add the Fringe row
      if (includeFringeDetails) {
        rows.push({
          rowData: { label: 'Fringe Total', value: fringeTotal },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        });
      }

      // Add contractual charges rows
      sortedContractualCharges.forEach((charge, index) => {
        rows.push({
          rowData: {
            label: `${charge.name}${
              charge.charge_type === 'percentage'
                ? ` (${formatPercentage(charge.percentage)}%)`
                : ' (Flat Fee)'
            }`,
            value: charge?.total_cost || 0,
          },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        });
      });

      rows.push({
        rowData: {
          label: 'Grand Total',
          value: budgetTotal + fringeTotal + contractualChargesTotal,
        },
        type: 'swimlaneFooter',
        renderMethod: renderSwimlaneSummaryFooter,
      });

      rows.push(
        ...appendFilmIncentiveRows(
          [],
          budgetTotal + fringeTotal + contractualChargesTotal
        )
      );

      if (showActualTotal) {
        rows.push({
          rowData: {
            label: 'Actual Total',
            value: actualSummary.actualTotal,
          },
          type: 'swimlaneFooter',
          renderMethod: renderSwimlaneSummaryFooter,
        });
      }

      return rows;
    };

    const getDetailedView = () => {
      const allRows = tableRows(budgetData?.budget_swimlane);
      const renderMethods = {
        'swimlane-row': renderSwimlaneDetails,
        'line-item-row': renderLineItemDetails,
        'detailed-sheet-row': renderDetailSheet,
      };

      return allRows?.map(item => {
        return {
          rowData: item.rowData,
          type: item.type,
          renderMethod: (rData, tabIndex) =>
            renderMethods[item.type]?.(
              item.key + '-' + tabIndex,
              item.rowData,
              item.type,
              item.colSpan > 0 ? item.colSpan : ''
            ),
        };
      });
    };
    const hasBudgetCategories = (budgetData?.budget_swimlane?.length || 0) > 0;

    return (
      <>
        <div
          className={`budget-data-container ${printPaperClassName}`}
          ref={ref}
          style={printPaperStyle}
        >
          {isLoading && (
            <CornerSpinner isLoading size="sm" label="Preparing budget PDF" />
          )}
          <SwimlanePage
            tableHeader={swimlaneSummaryHeader()}
            tableData={
              includeFringeDetails
                ? getSwimlaneSummaryWithFringes()
                : getSwimlaneSummaryWithoutFringes()
            }
            printLayoutFooter={printLayoutFooter()}
            printLayoutHeader={printLayoutHeader()}
            productionDetails={renderProductionDetails()}
            showTitle={true}
            tableClassName={'budget-details-table'}
            tableProps={{ striped: true, bordered: true, hover: true }}
            paperSize={paperSize}
            paperOptions={paperOptions}
          />
          {includeProductionAssumptions && (
            <>
              <div className="page-break" />
              <ProductionAssumptionsPage
                title={productionAssumptionsTitle}
                content={productionAssumptions}
                onTitleChange={onProductionAssumptionsTitleChange}
                onContentChange={onProductionAssumptionsChange}
                canEdit={canEditProductionAssumptions}
                printLayoutFooter={printLayoutFooter()}
                printLayoutHeader={printLayoutHeader()}
              />
            </>
          )}
          {hasBudgetCategories && (
            <>
              <div className="page-break" />
              <SwimlanePage
                tableHeader={detailsTableHeader()}
                printLayoutFooter={printLayoutFooter()}
                printLayoutHeader={printLayoutHeader()}
                tableData={getDetailedView()}
                tableClassName={'budget-details-sheet-table'}
                tableProps={{ striped: true, bordered: true, hover: true }}
                columnWidths={detailColumnWidths}
                footer={getBudgetFooterRows()}
                paperSize={paperSize}
                paperOptions={paperOptions}
              />
            </>
          )}
          {includeUsedGlobals && usedGlobals?.length > 0 && (
            <>
              <div className="page-break" />
              <UsedGlobalsPage
                globals={usedGlobals}
                printLayoutFooter={printLayoutFooter()}
                printLayoutHeader={printLayoutHeader()}
              />
            </>
          )}
          {/* <SwimlanePage
                tableHeader={null}
                tableData={getBudgetFooterRows()}
                printLayoutFooter={printLayoutFooter()}
                // printLayoutHeader={printLayoutHeader()}
                // productionDetails={''}
                showTitle={false}
                tableClassName={'budget-details-table'}
                tableProps={{ striped: true, bordered: true, hover: true }}
              /> */}
        </div>
      </>
    );
  }
);

MotionPictureBudget.displayName = 'MotionPictureBudget';

MotionPictureBudget.defaultProps = {
  budgetData: [],
  paperSize: 'letter',
  paperOptions: undefined,
  includeCineBudgetBranding: true,
};

MotionPictureBudget.propTypes = {
  paperSize: PropTypes.string,
  paperOptions: PropTypes.array,
  includeComments: PropTypes.bool.isRequired,
  includeCineBudgetBranding: PropTypes.bool,
  showProductionInfoHeaders: PropTypes.bool.isRequired,
  showJobInformationSection: PropTypes.bool.isRequired,
  showProductionInformationSection: PropTypes.bool.isRequired,
  jobInformationSection: PropTypes.object.isRequired,
  productionInformationSection: PropTypes.object.isRequired,
  onJobInformationChange: PropTypes.func.isRequired,
  onProductionInformationChange: PropTypes.func.isRequired,
  canEditProductionInfoSections: PropTypes.bool.isRequired,
  includeProductionAssumptions: PropTypes.bool.isRequired,
  productionAssumptionsTitle: PropTypes.string.isRequired,
  productionAssumptions: PropTypes.string.isRequired,
  onProductionAssumptionsTitleChange: PropTypes.func.isRequired,
  onProductionAssumptionsChange: PropTypes.func.isRequired,
  canEditProductionAssumptions: PropTypes.bool.isRequired,
  includeUsedGlobals: PropTypes.bool.isRequired,
  usedGlobals: PropTypes.array.isRequired,
  includeAccountNumbers: PropTypes.bool.isRequired,
  includeActuals: PropTypes.bool.isRequired,
  budgetData: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  getLabel: PropTypes.func.isRequired,
  includeFringeDetails: PropTypes.bool.isRequired,
  includeOvertimeDetails: PropTypes.bool.isRequired,
  visibleOvertimeSlots: PropTypes.object.isRequired,
  visibleDetailFields: PropTypes.object.isRequired,
};

export default MotionPictureBudget;
